import React, { useState, useCallback } from "react";
import { connect } from "react-redux";
import { change, formValueSelector } from "redux-form";
import { get } from "lodash";
import T from "prop-types";
import { Map, fromJS } from "immutable";
import { validator } from "LIB/validate-this";
import * as rules from "LIB/validation/rules";
import { manageFields, translateProps } from "MODULES/shared/decorators";
import { form, Form as AppForm } from "MODULES/shared/components/Forms";
import { withSalesOrder } from "MODULES/SalesOrders/decorators";
import { Row, Col } from "react-bootstrap";
import SellersSection from "MODULES/SalesOrders/components/shared/FormSections/SellersSection";
import ConditionWarning from "MODULES/SalesOrders/components/shared/Warnings/ConditionWarning";
import { withFieldAccessors } from "MODULES/shared/decorators/withFieldAccessors";
import {
  CORE_RETURN_DATE,
  CORE_DUE_DAYS
} from "MODULES/RepairOrders/constants";
import {
  ACCOUNTING,
  QUALITY_CONTROL,
  SALES,
  WAREHOUSE_EXPERT
} from "MODULES/users/constants";
import withCurrentUser, {
  WithCurrentUser
} from "MODULES/session/decorators/withCurrentUser";
import {
  CONDITION,
  OVERHAULED,
  REPAIRED,
  INSPECTED,
  NEW_ITEM,
  WARRANTY,
  INVENTORY
} from "MODULES/inventories/constants";
import {
  CAN_EXCHANGE,
  CANCELLED,
  COMPANY,
  ID,
  INVENTORY_ID
} from "APP_ROOT/constants";
import { EXCHANGE_FEE, PRICE_PER_UNIT } from "MODULES/OutgoingQuotes/constants";
import {
  CUSTOMER_QC_PREAPPROVAL_REQUIRED,
  DOLLARS,
  EXCHANGE_SALE,
  FLAT_EXCHANGE,
  OUTRIGHT_VALUE,
  PERCENTAGE,
  REPAIR_ADMINISTRATION_CHARGE_DOLLARS,
  REPAIR_ADMINISTRATION_CHARGE_PERCENTAGE,
  REPAIR_FEE,
  SELLER_ID
} from "MODULES/SalesOrders/constants";
import { CONTAINER_FEE, HAZMAT_FEE } from "MODULES/PurchaseOrders/constants";
import {
  hasAnyOfRoles,
  hasAnyOfRolesOrSubroles,
  hasRole
} from "MODULES/users/helpers";
import ExistingQuoteWarning from "MODULES/SalesOrders/components/ConvertFromCartForm/ExistingQuoteWarning";
import { warrantyForCondition } from "MODULES/OutgoingQuotes/shared/PartDetails";
import RestrictedEntityWarning from "MODULES/companies/shared/components/RestrictedEntityWarning";
import PartDetails from "./PartDetails";
import ExchangeSale from "./ExchangeSale";
import Shipping from "./Shipping";
import Notes from "./Notes";
import Summary from "./Summary";
import CancellationReason from "./CancellationReason";
import {
  BlanketExchangeAgreementWarning,
  NoPmaNoDerWarning,
  TraceTypeWarning
} from "../../../../ConvertFromCartForm/Warnings";
import { shouldSetQcPreapproval } from "../../../../../helpers";

export const formName = "salesOrderLineItem";

const sep = <hr className="hr-sm" />;

export function Form({
  activeExchange,
  condition,
  currentUser,
  settings,
  disableQcPreapproval,
  exchangeSale,
  exchanged,
  flatExchange,
  getFieldValue,
  lineItem,
  nonAccountingUser = true,
  salesOrder,
  selectedInventory,
  showTable = true,
  updateInventory,
  newRecord,
  editing,
  resetSpecialContainerFeeField,
  ...props
}) {
  const [part, setPart] = useState(
    lineItem.isEmpty() ? null : lineItem.get("part").toJS()
  );
  const cancelled = lineItem.get(CANCELLED);
  const shipped = lineItem.get("shipped");
  const paid = lineItem.get("invoice_paid_date");
  const canEditSeller =
    hasAnyOfRoles(currentUser, [SALES, ACCOUNTING, QUALITY_CONTROL]) ||
    currentUser.id === lineItem.get(SELLER_ID);
  const canEditPartDetails = hasRole(currentUser, SALES);
  const canEditQcPreapproval = hasAnyOfRolesOrSubroles(
    currentUser,
    [ACCOUNTING, QUALITY_CONTROL],
    [WAREHOUSE_EXPERT]
  );
  const commissionPaid = lineItem.get("commission_paid");

  const editable = canEditSeller || (!cancelled && !exchanged && !paid);

  const inventory = selectedInventory
    ? fromJS(selectedInventory)
    : lineItem.get(INVENTORY, Map());

  const company = salesOrder.get(COMPANY).toJS();

  const conditionChanged =
    inventory.get(ID) && inventory.get(CONDITION) !== condition;

  const setFieldValue = useCallback((field, newValue) => {
    props.setFieldValue(field)(newValue);
  }, []);
  return (
    <AppForm {...props} editable={editable || cancelled}>
      <AppForm.Body>
        <Row>
          <Col md={12} style={{ marginTop: "10px" }}>
            {conditionChanged && <ConditionWarning />}
            <NoPmaNoDerWarning
              company={company}
              inventories={[inventory.toJS]}
            />
            <TraceTypeWarning
              company={company}
              inventories={[inventory.toJS()]}
            />
            <BlanketExchangeAgreementWarning
              company={company}
              exchangeSale={exchangeSale}
            />
            <ExistingQuoteWarning
              part={part}
              company={company}
              sellerId={getFieldValue(SELLER_ID) || lineItem.get(SELLER_ID)}
            />
          </Col>
        </Row>
        <div className="nav nav-tabs">
          <h4>Part Details</h4>
        </div>
        <Row>
          <Col md={8}>
            <div className="tab-content">
              <div role="tabpanel" className="tab-pane active">
                <PartDetails
                  part={part}
                  lineItem={lineItem}
                  cancelled={cancelled}
                  paid={paid}
                  commissionPaid={commissionPaid}
                  showTable={showTable}
                  shipped={shipped}
                  exchanged={exchanged}
                  onInventorySelected={updateInventory}
                  canEditPartDetails={canEditPartDetails}
                  canEditQcPreapproval={canEditQcPreapproval}
                  disableQcPreapproval={disableQcPreapproval}
                  canEditHazmatFlags={hasRole(currentUser, ACCOUNTING)}
                  formName={formName}
                  resetSpecialContainerFeeField={resetSpecialContainerFeeField}
                  setPart={setPart}
                  {...props}
                />
                {sep}
                <h4>Sellers</h4>
                <RestrictedEntityWarning
                  company={company}
                  allContacts
                  requireApproval
                />
                <SellersSection
                  exchanged={exchanged}
                  disabled={!canEditSeller}
                  paid={paid}
                  {...props}
                />
                {sep}
                <ExchangeSale
                  activeExchange={activeExchange}
                  canExchange={get(settings, CAN_EXCHANGE)}
                  flatExchange={flatExchange}
                  company={company}
                  exchanged={exchanged}
                  cancelled={cancelled}
                  lineItem={lineItem}
                  nonAccountingUser={nonAccountingUser}
                  shipped={shipped}
                  paid={paid}
                  inventory={inventory}
                  canEditPartDetails={canEditPartDetails}
                  commissionPaid={commissionPaid}
                  newRecord={newRecord}
                  initialReturnValue={lineItem.get(CORE_RETURN_DATE)}
                  initialDayValue={lineItem.get(CORE_DUE_DAYS)}
                  setFieldValue={setFieldValue}
                />
                <Shipping
                  exchanged={exchanged}
                  cancelled={cancelled}
                  paid={paid}
                  shipped={shipped}
                  canEditPartDetails={canEditPartDetails}
                  canEditOutgoingAwb={hasRole(currentUser, ACCOUNTING)}
                  commissionPaid={commissionPaid}
                  part={part}
                />
                {sep}
                <Notes
                  editing={editing}
                  lineItem={lineItem}
                  canEditNotes={canEditPartDetails}
                  canEditInternalNotes={hasRole(currentUser, ACCOUNTING)}
                />
                {cancelled && <CancellationReason />}
              </div>
            </div>
          </Col>
          <Col md={4}>
            <Summary lineItem={lineItem} inventory={inventory} />
          </Col>
        </Row>
        <Row>
          <Col md={12} style={{ marginTop: "10px" }}>
            {conditionChanged && <ConditionWarning />}
          </Col>
        </Row>
      </AppForm.Body>
    </AppForm>
  );
}

Form.propTypes = {
  activeExchange: T.bool.isRequired,
  condition: T.string,
  exchanged: T.bool.isRequired,
  getFieldValue: T.bool,
  lineItem: T.instanceOf(Map).isRequired,
  nonAccountingUser: T.bool,
  salesOrder: T.instanceOf(Map),
  selectedInventory: T.shape({}),
  showTable: T.bool,
  updateInventory: T.func.isRequired,
  newRecord: T.bool,
  resetSpecialContainerFeeField: T.func,
  ...WithCurrentUser
};

const validate = values =>
  validator(values, f => {
    if (values.flat_exchange) {
      f.validate(REPAIR_FEE).satisfies(rules.required);
    }
    f.validate(INVENTORY_ID, CONDITION).satisfies(rules.required);
    if (values.exchange_sale) {
      f.validate(OUTRIGHT_VALUE, EXCHANGE_FEE).satisfies(rules.required);

      if (values.repair_admin_charge_type === PERCENTAGE) {
        f
          .validate(REPAIR_ADMINISTRATION_CHARGE_PERCENTAGE)
          .satisfies(
            rules.required,
            rules.isNumeric,
            rules.isLessThanOrEqualTo(100),
            rules.isGreaterThanOrEqualTo(1)
          );
      } else if (values.repair_admin_charge_type === DOLLARS) {
        f
          .validate(REPAIR_ADMINISTRATION_CHARGE_DOLLARS)
          .satisfies(rules.required, rules.isNumeric);
      }
    } else {
      f.validate(PRICE_PER_UNIT).satisfies(rules.required);
    }
    if (values.customer_to_return_container) {
      f.validate(CONTAINER_FEE).satisfies(rules.required);
    }
    f
      .validate(
        PRICE_PER_UNIT,
        "core_repair_charges",
        REPAIR_FEE,
        EXCHANGE_FEE,
        OUTRIGHT_VALUE,
        HAZMAT_FEE
      )
      .satisfies(rules.isNumeric);
  });

const enhancedForm = form({
  form: formName,
  validate,
  onSubmitSuccess: (
    _r,
    _d,
    { salesOrder, expireLineItems, close, refresh }
  ) => {
    refresh(salesOrder.get(ID)).then(() => {
      expireLineItems();
      close();
    });
  }
});

const withSelectedInventory = manageFields(
  {
    selectedInventory: null
  },
  ({ updateField, newRecord, salesOrder }) => ({
    updateInventory: inventory => {
      const defaultWarranty =
        inventory &&
        warrantyForCondition(
          inventory.condition,
          inventory.part,
          inventory.tag_date
        );

      updateField("selectedInventory")(inventory);

      if (newRecord) {
        const company = salesOrder.get("company").toJS();
        shouldSetQcPreapproval(company, inventory, () => {
          change(CUSTOMER_QC_PREAPPROVAL_REQUIRED, true);
          updateField("disableQcPreapproval")(true);
        });
      }

      if ([REPAIRED, INSPECTED, OVERHAULED].includes(inventory.condition)) {
        if (inventory.warranty_override) {
          change(WARRANTY, inventory.warranty_override);
        } else if (inventory.part.warranty_override) {
          change(WARRANTY, inventory.part.warranty_override);
        } else {
          change(WARRANTY, defaultWarranty);
        }
      } else if (
        inventory.condition === NEW_ITEM &&
        inventory.warranty_override
      ) {
        change(WARRANTY, inventory.warranty_override);
      } else {
        change(WARRANTY, defaultWarranty);
      }
    }
  })
);

const selector = formValueSelector(formName);
const withConditionAndExchangeAndSpecialContainer = Component =>
  connect(
    state => ({
      condition: selector(state, CONDITION),
      exchangeSale: selector(state, EXCHANGE_SALE),
      flatExchange: selector(state, FLAT_EXCHANGE),
      getFieldValue: field => selector(state, field)
    }),
    dispatch => ({
      resetSpecialContainerFeeField: fee => {
        dispatch(change(formName, CONTAINER_FEE, Number(fee)));
      }
    })
  )(Component);

const withExchange = translateProps(({ salesOrder, lineItem }) => ({
  exchanged: salesOrder.get("has_exchange"),
  activeExchange: lineItem.get(EXCHANGE_SALE, false)
}));

export default withSalesOrder(
  withCurrentUser(
    withConditionAndExchangeAndSpecialContainer(
      enhancedForm(
        withFieldAccessors(withExchange(withSelectedInventory(Form)))
      )
    )
  )
);



// WEBPACK FOOTER //
// ./app/modules/SalesOrders/components/Detail/Body/LineItems/Form/Form.jsx